import {useEffect, useState, useRef, useMemo, useCallback} from "react"

/**
 * @typedef {"idle"|"verifying"|"verified"|"error"} VerificationState
 */

/**
 * @typedef {Object} ActionConsumerResult
 * @property {Object<string, Object>} actionFields - Map of field key to field config with populated options
 * @property {Object<string, boolean>} actionLoading - Map of action name to loading state
 * @property {Object<string, string|null>} actionErrors - Map of action name to error message
 * @property {(changedKey: string) => Object<string, null>} clearDependents - Returns map of dependent field keys to null for clearing
 * @property {(currentValues: Object) => Object} captureLabelValues - Extracts label values from select/multi-select fields based on capture_label_as config
 * @property {() => void} reset - Reset all action state (fields, loading, errors, verification)
 * @property {VerificationState} verificationState - Current state of credential verification
 * @property {string|null} verifyError - Error message from the most recent verify() call, or null
 * @property {() => Promise<void>} verify - Fire all eligible non-cascade actions and transition verification state
 */

/**
 * Build a clearDependents function for given actions config.
 * Pure function — no React hooks, testable in isolation.
 *
 * @param {Array|null} actions - Array of action config objects
 * @returns {(changedKey: string) => Object<string, null>} Function that returns map of dependent field keys to clear
 */
export function buildClearDependents(actions) {
  return (changedKey) => {
    if (!actions || !actions.length) return {}

    const cleared = {}
    for (const action of actions) {
      if (action.trigger_fields.includes(changedKey)) {
        for (const fieldKey of Object.keys(action.populates)) {
          cleared[fieldKey] = null
        }
      }
      for (const [fieldKey, fieldConfig] of Object.entries(action.populates)) {
        if (fieldConfig.depends_on === changedKey) {
          cleared[fieldKey] = null
        }
      }
    }
    return cleared
  }
}

/**
 * Build a captureLabelValues function for given actions config.
 * Pure function — no React hooks, testable in isolation.
 *
 * @param {Array|null} actions - Array of action config objects
 * @returns {(currentValues: Object) => Object} Function that extracts label values from select fields
 */
export function buildCaptureLabelValues(actions) {
  return (currentValues) => {
    if (!actions || !actions.length) return {}

    const captured = {}
    for (const action of actions) {
      for (const [fieldKey, fieldConfig] of Object.entries(action.populates)) {
        if (!fieldConfig.capture_label_as) continue

        const val = currentValues[fieldKey]
        if (val == null) continue

        if (fieldConfig.type === "multi-select" && Array.isArray(val)) {
          captured[fieldConfig.capture_label_as] = val
            .filter(v => v != null)
            .map(v => (v && typeof v === "object" && "label" in v) ? v.label : v)
            .join(",")
        } else if (val && typeof val === "object" && "label" in val) {
          captured[fieldConfig.capture_label_as] = val.label
        }
      }
    }
    return captured
  }
}

/**
 * Determine whether an action is a "cascade" action — one whose trigger_fields
 * include a key that is populated by some other action. Cascade actions depend
 * on verify() having run first; they auto-fire after verification.
 *
 * @param {Object} action - Single action config
 * @param {Array} allActions - All action configs for the integration
 * @returns {boolean} True if action is a cascade action
 */
export function isCascadeAction(action, allActions) {
  const allPopulatedKeys = new Set()
  for (const a of allActions) {
    if (a === action) continue
    for (const key of Object.keys(a.populates || {})) {
      allPopulatedKeys.add(key)
    }
  }
  return action.trigger_fields.some(tf => allPopulatedKeys.has(tf))
}

/**
 * Build the payload for an action by resolving $field references against parsed trigger values.
 *
 * @param {Object} action - Action config with payload template
 * @param {Object} parsedTriggers - Map of field key to resolved value
 * @returns {Object} Resolved payload
 */
function buildPayload(action, parsedTriggers) {
  const payload = {}
  for (const [payloadKey, payloadValue] of Object.entries(action.payload)) {
    if (typeof payloadValue === "string" && payloadValue.startsWith("$")) {
      const fieldKey = payloadValue.slice(1)
      payload[payloadKey] = parsedTriggers[fieldKey]
    } else {
      payload[payloadKey] = payloadValue
    }
  }
  return payload
}

/**
 * Map an action response to actionFields entries.
 *
 * @param {Object} action - Action config with populates
 * @param {Object} response - API response with data.data array
 * @returns {Object} Map of field key to field config with options
 */
function mapResponseToFields(action, response) {
  const items = response.data?.data || []
  const newFields = {}
  for (const [fieldKey, fieldConfig] of Object.entries(action.populates)) {
    const options = items.map(item => ({
      value: item[fieldConfig.source_key],
      label: item[fieldConfig.source_label],
    }))
    newFields[fieldKey] = {
      ...fieldConfig,
      options,
    }
  }
  return newFields
}

/**
 * Hook that manages action-driven field population with an explicit verify gate.
 *
 * Non-cascade actions (credential-level) require an explicit verify() call.
 * Cascade actions (depend on populated fields) auto-fire after verification
 * using debounce + AbortController.
 *
 * @param {Array|undefined} actions - Array of action config objects from the integration
 * @param {Object} values - Current form field values
 * @param {Function} onActionCall - Callback to fire action API calls: (path, payload, signal) => Promise
 * @returns {ActionConsumerResult} Hook result with action fields, loading/error states, and helpers
 */
export function useActionConsumer(actions, values, onActionCall) {
  const [actionFields, setActionFields] = useState({})
  const [actionLoading, setActionLoading] = useState({})
  const [actionErrors, setActionErrors] = useState({})
  const [verificationState, setVerificationState] = useState("idle")
  const [verifyError, setVerifyError] = useState(null)
  const controllersRef = useRef({})

  const triggerFingerprint = useMemo(() => {
    if (!actions || !actions.length) return ""
    const triggerKeys = new Set(actions.flatMap(a => a.trigger_fields))
    const relevant = {}
    for (const k of triggerKeys) {
      const v = values[k]
      relevant[k] = v && typeof v === "object" && "value" in v ? v.value : v
    }
    return JSON.stringify(relevant)
  }, [actions, values])

  // verify() fires all eligible non-cascade actions in parallel
  const verify = useCallback(async () => {
    if (verificationState === "verifying") return
    if (!actions || !actions.length || !onActionCall) return

    const parsedTriggers = triggerFingerprint ? JSON.parse(triggerFingerprint) : {}

    // Collect non-cascade actions whose trigger_fields are all filled
    const eligible = actions.filter(action => {
      if (isCascadeAction(action, actions)) return false
      return action.trigger_fields.every(k => {
        const v = parsedTriggers[k]
        return v != null && v !== ""
      })
    })

    if (eligible.length === 0) return

    setVerificationState("verifying")
    setVerifyError(null)

    const controller = new AbortController()
    controllersRef.current.__verify = controller

    try {
      const results = await Promise.all(
        eligible.map(action => {
          const payload = buildPayload(action, parsedTriggers)
          setActionLoading(prev => ({...prev, [action.name]: true}))
          setActionErrors(prev => ({...prev, [action.name]: null}))
          return onActionCall(action.path, payload, controller.signal)
            .then(response => ({action, response, error: null}))
            .catch(e => ({action, response: null, error: e}))
        })
      )

      if (controller.signal.aborted) return

      const anyFailure = results.find(r => r.error !== null)

      if (anyFailure) {
        const e = anyFailure.error
        const message = e.error?.response?.data?.error?.message || "Failed to load options"
        setVerificationState("error")
        setVerifyError(message)

        // Update per-action loading/error states
        for (const r of results) {
          if (r.error) {
            const errMsg = r.error.error?.response?.data?.error?.message || "Failed to load options"
            setActionErrors(prev => ({...prev, [r.action.name]: errMsg}))
          }
          setActionLoading(prev => ({...prev, [r.action.name]: false}))
        }
      } else {
        // All succeeded — populate action fields
        let allNewFields = {}
        for (const r of results) {
          const newFields = mapResponseToFields(r.action, r.response)
          allNewFields = {...allNewFields, ...newFields}
          setActionLoading(prev => ({...prev, [r.action.name]: false}))
        }
        setActionFields(prev => ({...prev, ...allNewFields}))
        setVerificationState("verified")
      }
    } catch (e) {
      if (!controller.signal.aborted) {
        const message = e.error?.response?.data?.error?.message || "Failed to load options"
        setVerificationState("error")
        setVerifyError(message)
      }
    }
  }, [actions, triggerFingerprint, onActionCall, verificationState])

  // Auto-fire cascade actions only after verification
  useEffect(() => {
    if (!actions || !actions.length || !onActionCall) return
    if (verificationState !== "verified") return

    const parsedTriggers = triggerFingerprint ? JSON.parse(triggerFingerprint) : {}

    const timers = []

    for (const action of actions) {
      if (!isCascadeAction(action, actions)) continue

      const allTriggersFilled = action.trigger_fields.every(k => {
        const v = parsedTriggers[k]
        return v != null && v !== ""
      })

      if (!allTriggersFilled) {
        // Clear populated fields for this action when triggers are incomplete
        // Return prev unchanged if no keys need clearing to avoid infinite re-render loop
        setActionFields(prev => {
          const keysToDelete = Object.keys(action.populates).filter(k => k in prev)
          if (keysToDelete.length === 0) return prev
          const next = {...prev}
          for (const key of keysToDelete) {
            delete next[key]
          }
          return next
        })
        setActionErrors(prev => {
          if (prev[action.name] === null || prev[action.name] === undefined) return prev
          return {...prev, [action.name]: null}
        })
        continue
      }

      const timer = setTimeout(() => {
        // Abort previous request for this action
        if (controllersRef.current[action.name]) {
          controllersRef.current[action.name].abort()
        }

        const controller = new AbortController()
        controllersRef.current[action.name] = controller

        const payload = buildPayload(action, parsedTriggers)

        setActionLoading(prev => ({...prev, [action.name]: true}))
        setActionErrors(prev => ({...prev, [action.name]: null}))

        onActionCall(action.path, payload, controller.signal)
          .then(response => {
            if (controller.signal.aborted) return
            const newFields = mapResponseToFields(action, response)
            setActionFields(prev => ({...prev, ...newFields}))
            setActionLoading(prev => ({...prev, [action.name]: false}))
          })
          .catch(e => {
            // Aborted requests (from typing or modal close) are not errors
            if (controller.signal.aborted || e.handled) {
              setActionLoading(prev => ({...prev, [action.name]: false}))
              return
            }

            const message = e.error?.response?.data?.error?.message || "Failed to load options"
            setActionErrors(prev => ({...prev, [action.name]: message}))
            setActionLoading(prev => ({...prev, [action.name]: false}))
          })
      }, 500)

      timers.push(timer)
    }

    // Capture ref value at effect time for react-hooks/exhaustive-deps rule
    const controllers = controllersRef.current
    return () => {
      for (const timer of timers) {
        clearTimeout(timer)
      }
      for (const controller of Object.values(controllers)) {
        controller.abort()
      }
    }
  }, [actions, triggerFingerprint, onActionCall, verificationState])

  const clearDependentsBase = useMemo(() => buildClearDependents(actions), [actions])

  const clearDependents = useCallback((changedKey) => {
    const cleared = clearDependentsBase(changedKey)

    if (Object.keys(cleared).length > 0) {
      setActionFields(prev => {
        const next = {...prev}
        for (const key of Object.keys(cleared)) {
          delete next[key]
        }
        return next
      })
    }

    return cleared
  }, [clearDependentsBase])

  const captureLabelValues = useMemo(() => buildCaptureLabelValues(actions), [actions])

  const reset = useCallback(() => {
    if (controllersRef.current.__verify) {
      controllersRef.current.__verify.abort()
    }
    setActionFields({})
    setActionLoading({})
    setActionErrors({})
    setVerificationState("idle")
    setVerifyError(null)
  }, [])

  return {
    actionFields, actionLoading, actionErrors,
    clearDependents, captureLabelValues, reset,
    verificationState, verifyError, verify,
  }
}
